/**
 * Writes, the toast that reports a refused one, and who "you" are.
 *
 * A write is `POST /api/mutate {name, args, user}`; the Worker runs it as a transaction on r2db.
 * There is no optimistic copy in the page: the change appears when the live queries watching it
 * receive it, which is the honest latency of this design — and usually well under a second.
 */
import { useSyncExternalStore } from 'react';

// ── The toast ───────────────────────────────────────────────────────────────────────────────
let toastText: string | null = null;
let toastTimer: ReturnType<typeof setTimeout> | undefined;
const toastWatchers = new Set<() => void>();
export function toast(text: string) {
  toastText = text;
  for (const w of toastWatchers) w();
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastText = null; for (const w of toastWatchers) w(); }, 5200);
}
export function useToast(): string | null {
  return useSyncExternalStore(cb => { toastWatchers.add(cb); return () => toastWatchers.delete(cb); }, () => toastText);
}

// ── You ─────────────────────────────────────────────────────────────────────────────────────
// A handle per browser, kept in localStorage. It is not authentication — anyone can type any name —
// and it does not need to be: it decides whose issues the delete button works on, which is the
// demo's owner check, and whose name goes on a comment.
const USER_KEY = 'r2db-issues.user';
function readUser(): string {
  try {
    const saved = localStorage.getItem(USER_KEY);
    if (saved) return saved;
    const fresh = `guest-${Math.random().toString(36).slice(2, 6)}`;
    localStorage.setItem(USER_KEY, fresh);
    return fresh;
  } catch { return 'guest'; }
}
let user = readUser();
const userWatchers = new Set<() => void>();
export function setUser(next: string) {
  user = next;
  try { localStorage.setItem(USER_KEY, next); } catch { /* private window: keep it for this tab */ }
  for (const w of userWatchers) w();
}
export function useUser(): string {
  return useSyncExternalStore(cb => { userWatchers.add(cb); return () => userWatchers.delete(cb); }, () => user);
}

// ── Writes ──────────────────────────────────────────────────────────────────────────────────
export async function mutate<T = unknown>(name: string, args: unknown): Promise<T | undefined> {
  let response: Response;
  try {
    response = await fetch('/api/mutate', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, args, user }),
    });
  } catch {
    toast('Could not reach the server. Check your connection.');
    return undefined;
  }
  const body: any = await response.json().catch(() => ({}));
  if (!response.ok) {
    toast(body.error ?? `That did not save (${response.status}).`);
    return undefined;
  }
  return body.value as T;
}
